import { z } from "zod";

/**
 * FortiGate module validation.
 *
 * Two contracts live here:
 *
 *  • The single-device probe, which mirrors the n8n "Validate & Encode" node so
 *    the API rejects bad input with a clear message BEFORE forwarding (and so the
 *    frontend and the n8n flow share one contract):
 *      – address — hostname or IP, optionally `:port`. The n8n regex forbids a
 *        scheme, so any leading `http(s)://` (and trailing slashes) is stripped
 *        here first — the UI may present the friendlier `https://…` form, but the
 *        value forwarded upstream is always scheme-less.
 *      – apiKey  — 20–128 chars of [A-Za-z0-9_-] (the FortiGate REST token shape).
 *
 *  • The monitoring programme (migration 0028): the schedule patch the Settings
 *    panel sends. Every bound here is also a database CHECK — this layer exists
 *    to turn a constraint violation into a field-level message, not to be the
 *    only guard.
 */

const addressPattern = /^[a-zA-Z0-9.-]+(:[0-9]{1,5})?$/;
const apiKeyPattern = /^[A-Za-z0-9_-]{20,128}$/;

/** Hostname/IP (optionally :port), with any scheme + trailing slash stripped. */
const fortiAddress = z.preprocess(
  (value) =>
    typeof value === "string"
      ? value.trim().replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, "").replace(/\/+$/, "")
      : value,
  z
    .string()
    .regex(
      addressPattern,
      "Enter a hostname or IP address, optionally with :port (without https://)",
    ),
);

const fortiApiKey = z
  .string()
  .trim()
  .regex(
    apiKeyPattern,
    "API key must be 20–128 characters using letters, digits, _ or -",
  );

/** POST /api/v1/fortigate/check body. */
export const fortiGateCheckSchema = z.object({
  address: fortiAddress,
  apiKey: fortiApiKey,
});

export type FortiGateCheckInput = z.infer<typeof fortiGateCheckSchema>;

// ── The monitoring programme ────────────────────────────────────────────────

/**
 * An IANA timezone name. Validated by asking `Intl` to use it rather than by
 * pattern: the list changes with the tzdata the runtime ships, and a regex would
 * either reject valid zones or accept nonsense.
 */
const ianaTimeZone = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: value });
      return true;
    } catch {
      return false;
    }
  }, "Unknown timezone");

const reportRecipient = z
  .string()
  .trim()
  .toLowerCase()
  .email("Enter a valid email address")
  .max(254);

/**
 * Days of month the report fires on. Capped at 28 so February fires too — a
 * schedule that silently skips a month is worse than a fixed early date. Sorted
 * and de-duplicated here so "1, 15" and "15, 1, 15" store identically and the
 * claim key stays stable.
 */
const reportDays = z
  .array(z.number().int().min(1).max(28))
  .min(1, "Pick at least one day")
  .max(4, "At most four report days")
  .transform((days) => [...new Set(days)].sort((a, b) => a - b));

/**
 * PATCH /api/v1/fortigate/monitor/schedule body. Every field is optional — the
 * panel sends only what changed, and an absent field must never be read as
 * "clear it".
 */
export const fortiGateScheduleSchema = z
  .object({
    dailyEnabled: z.boolean(),
    dailyHour: z.number().int().min(0).max(23),
    reportEnabled: z.boolean(),
    reportDays,
    reportHour: z.number().int().min(0).max(23),
    // An explicit empty list is meaningful (stop emailing anyone), so this is
    // not `.min(1)`; the service refuses to ENABLE the report with no recipients.
    reportRecipients: z.array(reportRecipient).max(50).transform(
      (list) => [...new Set(list)],
    ),
    reportWhenClean: z.boolean(),
    timezone: ianaTimeZone,
    retentionDays: z.number().int().min(0).max(3650),
  })
  .partial()
  .refine(
    (patch) => Object.keys(patch).length > 0,
    "Provide at least one field to update",
  );

export type FortiGateScheduleInput = z.infer<typeof fortiGateScheduleSchema>;

/** GET /api/v1/fortigate/sweeps query. */
export const fortiGateSweepListSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

export type FortiGateSweepListInput = z.infer<typeof fortiGateSweepListSchema>;

/**
 * POST /api/v1/fortigate/monitor/report body — an on-demand send of the report
 * for a given sweep (defaults to the latest). `recipients` overrides the stored
 * list for this send only, which is how "send me a copy" works without editing
 * the programme.
 */
export const fortiGateSendReportSchema = z.object({
  sweepId: z.string().uuid().optional(),
  recipients: z.array(reportRecipient).min(1).max(50).optional(),
});

export type FortiGateSendReportInput = z.infer<typeof fortiGateSendReportSchema>;

// ── Operator notes ──────────────────────────────────────────────────────────

/**
 * PUT /api/v1/fortigate/notes body — the note on one device (migration 0039).
 *
 * `note` may be empty: that is how a note is CLEARED, and the service deletes the
 * row rather than storing a blank one. The company/customer/address fields are the
 * context of the row the operator was looking at; they are stored alongside so the
 * note stays readable after the device leaves the fleet, and they are optional
 * because the key alone is enough to file it.
 */
export const fortiGateNoteSchema = z.object({
  deviceKey: z.string().trim().min(1, "A device is required").max(300),
  note: z.string().trim().max(2000, "A note is limited to 2000 characters"),
  companyName: z.string().trim().max(300).optional(),
  customerId: z.string().trim().max(64).optional(),
  address: z.string().trim().max(300).optional(),
});

export type FortiGateNoteInput = z.infer<typeof fortiGateNoteSchema>;

/**
 * The shape one row of the upstream sweep payload takes. The n8n flow returns
 * whatever the PowerShell script produced, so every field is coerced and
 * defaulted rather than required: a single malformed row must degrade to
 * "Unknown", not discard the other forty-nine.
 */
export const fortiGateUpstreamRowSchema = z
  .object({
    CompanyName: z.unknown().optional(),
    "Customer ID": z.unknown().optional(),
    accprofile: z.unknown().optional(),
    Status: z.unknown().optional(),
    FortiAddress: z.unknown().optional(),
    StatusCode: z.unknown().optional(),
    Message: z.unknown().optional(),
  })
  .passthrough();
